import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { MapPin, Clock } from "lucide-react";
import type { DiningVenue } from "@/data/airportDining";

interface DiningTableProps {
  venues: DiningVenue[];
  /** Hide the terminal filter on terminal-specific pages. */
  showTerminalFilter?: boolean;
}

const chip = (active: boolean) =>
  `px-3 py-2 rounded-xl text-base min-h-[44px] active:scale-95 transition ${
    active
      ? "bg-primary text-primary-foreground"
      : "bg-muted text-muted-foreground hover:text-foreground"
  }`;

export const DiningTable = ({ venues, showTerminalFilter = true }: DiningTableProps) => {
  const [terminal, setTerminal] = useState("all");
  const [category, setCategory] = useState("all");

  const terminals = ["all", ...Array.from(new Set(venues.map((v) => v.terminal)))];
  const categories = ["all", ...Array.from(new Set(venues.map((v) => v.service)))];

  const filtered = venues.filter(
    (v) =>
      (terminal === "all" || v.terminal === terminal) &&
      (category === "all" || v.service === category)
  );

  return (
    <div className="space-y-4">
      {showTerminalFilter && terminals.length > 2 && (
        <div className="flex gap-2 flex-wrap" role="group" aria-label="Filter by terminal">
          {terminals.map((t) => (
            <button key={t} onClick={() => setTerminal(t)} className={chip(terminal === t)}>
              {t === "all" ? "All terminals" : t}
            </button>
          ))}
        </div>
      )}

      <div className="flex gap-2 flex-wrap" role="group" aria-label="Filter by type">
        {categories.map((c) => (
          <button key={c} onClick={() => setCategory(c)} className={chip(category === c)}>
            {c === "all" ? "All types" : c}
          </button>
        ))}
      </div>

      <ul className="divide-y border-t">
        {filtered.map((v) => (
          <li key={`${v.terminal}-${v.name}`} className="py-4 space-y-1.5">
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-base font-semibold text-foreground">{v.name}</h3>
              <div className="flex items-center gap-1.5 shrink-0">
                {v.local && <Badge className="shrink-0">Local</Badge>}
                <Badge variant="secondary" className="shrink-0">{v.price}</Badge>
              </div>
            </div>

            <p className="text-base text-muted-foreground">
              {v.cuisine} · {v.service}
            </p>

            <p className="text-base text-muted-foreground flex items-center gap-1.5">
              <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
              {v.terminal}
              {v.location ? `, ${v.location}` : ""} ·{" "}
              {v.security === "post" ? "after security" : "before security"}
            </p>

            <p className="text-base text-muted-foreground flex items-center gap-1.5">
              <Clock className="h-4 w-4 shrink-0" aria-hidden="true" />
              {v.hours}
            </p>

            {v.note && <p className="text-base text-foreground/80">{v.note}</p>}
          </li>
        ))}
      </ul>

      {filtered.length === 0 && (
        <p className="text-base text-muted-foreground py-4">
          Nothing matches those filters — try clearing one.
        </p>
      )}
    </div>
  );
};
